/* eslint-disable camelcase */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import { message, Form } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { filter, isEmpty, isEqual, toNumber } from 'lodash';
import {
  convertFormLayoutToComponentFields,
  getFormValue,
  processLocationFieldsArray,
  getNameFieldValue,
  sanitizeNameFields,
  getNameFieldMetadata,
} from 'Src/common/utilities/form_utils';
import history from 'Src/common/utilities/history';
import { INPUT_TYPES } from 'Src/common/constants';
import { PHONE_NUMBER_INPUT_PREFIX } from 'Src/adminFormsX/constants';

import { applyForMentorship, getPrefilledData, getUserProfile } from '../../actions';
import Footer from '../../components/onboarding/footer';
import StepOne from '../../components/onboarding/stepOne';
import StepTwo from '../../components/onboarding/stepTwo';
import { buildStepTwoPayload } from '../../components/onboarding/stepTwo/util';
import StepThreeAndFour from '../../components/onboarding/stepThree';
import StepFive from '../../components/onboarding/stepFive';
import handlePrefill from './util';
import VerificationWrapper from '../verificationWrapper';

function Onboarding() {
  const [step, setStep] = useState(1);
  const [fields, setFields] = useState([]);
  const [lastStep, setLastStep] = useState(false);
  const [isProfileSelectModalOpen, setIsProfileSelectModalOpen] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [registrationFormEntry, setRegistrationFormEntry] = useState([]);
  const [loading, setLoading] = useState(false);
  const isProfileSelected = useRef(false);
  const forcedRoleRef = useRef(null);
  const hasPersistedForcedRoleRef = useRef(false);
  const stepTwoValuesChangeHandlerRef = useRef(null);
  const [form] = Form.useForm();
  const selectedMentorshipTypes = form.getFieldValue('mentorship_type') || [];
  const totalSteps = forcedRoleRef.current
    ? 3
    : selectedMentorshipTypes.length > 1
      ? 5
      : 4;
  const checkStep = (name) => form.getFieldValue('mentorship_type')?.includes(name);
  const { programDetails } = window;
  const profileInformationForm = programDetails?.profile_information_form || { fields: [] };

  const fetchPrefillData = (data = profileData) => {
    if (window.isUserAuthenticated) {
      const id = data.id || '';
      getPrefilledData(id)
        .then((res) => {
          if (forcedRoleRef.current && res.data?.is_completed) {
            history.push('/mentorship/connections');
            return;
          }

          const autoFillFields = handlePrefill(res.data, profileData, setProfileData);
          if (forcedRoleRef.current) {
            autoFillFields.mentorship_type = [forcedRoleRef.current];
          }
          form.setFieldsValue(autoFillFields);
          if (
            (forcedRoleRef.current === 'mentor' && res.data?.form_1_data?.is_mentor) ||
            (forcedRoleRef.current === 'mentee' && res.data?.form_1_data?.is_mentee)
          ) {
            hasPersistedForcedRoleRef.current = true;
          }
          if (!isEmpty(res.data?.form_5_data)) {
            const fieldEntries = res.data.form_5_data.field_entries;
            setRegistrationFormEntry(fieldEntries);
          }
        })
        .catch(() => {
          setProfileData((currentProfileData) => ({ ...currentProfileData, ...data }));
        });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forcedRole = params.get('role');
    const hasForcedRole = ['mentor', 'mentee'].includes(forcedRole);

    if (hasForcedRole) {
      forcedRoleRef.current = forcedRole;
      form.setFieldsValue({ mentorship_type: [forcedRole] });
      setStep(2);
    }

    if (params.get('step') === '0') {
      const profile_id = params.get('profile_id');

      if (profile_id) {
        setProfileData({ ...profileData, id: toNumber(profile_id) });

        getUserProfile(profile_id).then((res) => {
          fetchPrefillData(res.data);
        });
      } else {
        setIsProfileSelectModalOpen(true);
      }
    } else {
      fetchPrefillData();
    }

    const result = convertFormLayoutToComponentFields(programDetails.registration_form.fields);
    setFields(result);
  }, []);

  const handleSelectProfile = (profile) => {
    fetchPrefillData(profile);
    setStep(forcedRoleRef.current ? 2 : 1);
    isProfileSelected.current = true;
  };

  const persistForcedRoleSelection = () => {
    if (!forcedRoleRef.current || hasPersistedForcedRoleRef.current) {
      return Promise.resolve();
    }

    return applyForMentorship(
      {
        form_data: {
          is_mentor: forcedRoleRef.current === 'mentor',
          is_mentee: forcedRoleRef.current === 'mentee',
        },
        step: 1,
        mentorship_program: programDetails.id,
        role: forcedRoleRef.current,
      },
      profileData.id,
    ).then(() => {
      hasPersistedForcedRoleRef.current = true;
    });
  };

  const toggleProfileSelectModal = () => {
    if (isProfileSelected.current) {
      setIsProfileSelectModalOpen((prev) => !prev);
    } else {
      window.open('/admin/mentorship/members', '_self');
    }
  };

  const getFormData = async () => {
    if (step === 1) {
      return {
        is_mentor: checkStep('mentor'),
        is_mentee: checkStep('mentee'),
      };
    }

    if (step === 2) {
      return buildStepTwoPayload({
        formValues: form.getFieldsValue(true),
        fields: profileInformationForm.fields || [],
        profileData,
        profileId: profileData.id,
      });
    }

    if (step === 3) {
      const items = form.getFieldValue('mentor_services');

      return {
        services: items,
      };
    }

    if (step === 4) {
      const items = form.getFieldValue('mentee_services');

      return {
        services: items,
      };
    }

    return {};
  };

  const handleProceedClick = () => {
    form.validateFields().then(() => {
      if (step === 5) {
        setLastStep(true);
        return;
      }

      setLoading(true);

      persistForcedRoleSelection()
        .then(() => getFormData())
        .then((stepFormData) =>
          applyForMentorship(
            {
              form_data: stepFormData,
              step,
              mentorship_program: programDetails.id,
              ...(forcedRoleRef.current ? { role: forcedRoleRef.current } : {}),
            },
            profileData.id,
          ))
        .then(() => {
          setLoading(false);

          if (step === 2 && checkStep('mentor') && checkStep('mentee')) {
            setStep((prev) => prev + 1);
            return;
          }

          if (step === 2 && checkStep('mentee')) {
            setStep((prev) => prev + 2);
            return;
          }

          if (step === 3 && checkStep('mentor') && !checkStep('mentee')) {
            setStep((prev) => prev + 2);
            return;
          }

          setStep((prev) => prev + 1);
        })
        .catch(() => {
          setLoading(false);
        });
    });
  };

  const handleBackClick = () => {
    if (forcedRoleRef.current && step <= 2) {
      history.goBack();
      return;
    }

    if (step <= 1) {
      history.goBack();
      return;
    }

    if (checkStep('mentor') && checkStep('mentee')) {
      setStep((prev) => prev - 1);
      return;
    }

    if (step === 4 && checkStep('mentee')) {
      setStep((prev) => prev - 2);
      return;
    }

    if (step === 5 && checkStep('mentor')) {
      setStep((prev) => prev - 2);
      return;
    }

    setStep((prev) => prev - 1);
  };

  const submitLastStep = (customFormInstance) => {
    customFormInstance
      .validateFields()
      .then(() => {
        setLoading(true);
        let result = [];
        const fieldsXHash = {};
        const fieldsValue = sanitizeNameFields(customFormInstance.getFieldsValue());
        const fieldIds = Object.keys(fieldsValue);

        fields.forEach((field) => {
          fieldsXHash[field.id] = field;
        });

        fieldIds.forEach((fieldId) => {
          if (fieldsXHash[fieldId] && fieldsXHash[fieldId].type === INPUT_TYPES.NAME) {
            result.push({
              field_id: fieldId,
              value: getNameFieldValue(fieldsValue[fieldId], fieldsXHash[fieldId].options[0].value),
              custom_data: getNameFieldMetadata(fieldsValue[fieldId], fieldsXHash[fieldId].options[0].value),
            });
          } else {
            result.push({
              field_id: fieldId,
              value: getFormValue(fieldsValue[fieldId]),
            });
          }
        });

        const isPhoneNumberField = filter(fields, (field) => field.type === INPUT_TYPES.PHONE_NUMBER)[0];
        if (isPhoneNumberField) {
          result = filter(result, (data) => !data.field_id.includes(PHONE_NUMBER_INPUT_PREFIX));
        }
        result = processLocationFieldsArray(result);

        applyForMentorship(
          {
            form_data: {
              field_entries: result,
            },
            step,
            mentorship_program: programDetails.id,
            ...(forcedRoleRef.current ? { role: forcedRoleRef.current } : {}),
          },
          profileData.id,
        )
          .then(() => {
            const params = new URLSearchParams(window.location.search);

            if (params.has('profile_id') || params.has('step')) {
              message.success('Updated profile successfully!');
              history.goBack();
            } else if (params.has('edit')) {
              history.push({ pathname: '/mentorship/settings', search: '?update=true' });
            } else {
              history.push({ pathname: '/mentorship/mentors', search: '?new_user=true' });
            }
          })
          .catch(() => {
            setLoading(false);
            setLastStep(false);
          });
      })
      .catch(() => {
        setLastStep(false);
      });
  };

  return (
    <VerificationWrapper>
      <div className="mentorship-onboarding-wrapper" aria-live="polite">
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            stepTwoValuesChangeHandlerRef.current?.(changedValues, allValues);
          }}>
          <If condition={isEqual(step, 1)}>
            <StepOne
              totalSteps={totalSteps}
              toggleProfileSelectModal={toggleProfileSelectModal}
              handleSelectProfile={handleSelectProfile}
              isProfileSelectModalOpen={isProfileSelectModalOpen}
            />
          </If>
          <If condition={isEqual(step, 2)}>
            <StepTwo
              form={form}
              totalSteps={totalSteps}
              profileData={profileData}
              profileInformationForm={profileInformationForm}
              displayStep={forcedRoleRef.current ? 1 : 2}
              registerValuesChangeHandler={(handler) => {
                stepTwoValuesChangeHandlerRef.current = handler;
              }}
            />
          </If>
          <If condition={isEqual(step, 3)}>
            <StepThreeAndFour
              totalSteps={totalSteps}
              isMentor
              services={programDetails?.services}
              displayStep={forcedRoleRef.current ? 2 : null}
            />
          </If>
          <If condition={isEqual(step, 4)}>
            <StepThreeAndFour
              totalSteps={totalSteps}
              isMentor={false}
              services={programDetails?.services}
              displayStep={forcedRoleRef.current ? 2 : null}
            />
          </If>
          <If condition={isEqual(step, 5)}>
            <StepFive
              totalSteps={totalSteps}
              fields={fields}
              submitLastStep={submitLastStep}
              lastStep={lastStep}
              registrationFormEntry={isEmpty(registrationFormEntry) ? [] : registrationFormEntry}
            />
          </If>
          <Footer handleProceedClick={handleProceedClick} handleBackClick={handleBackClick} loading={loading} />
        </Form>
      </div>
    </VerificationWrapper>
  );
}

export default Onboarding;
